// =============================================================================
// ui/share/ShareDialog.tsx — what is asked BEFORE a link exists.
// -----------------------------------------------------------------------------
// ⚠ ASKED BEFORE THE ROW IS WRITTEN, and the ordering is the whole point.
// The old client's first version wrote the row and only then showed a dialog, so
// there was no moment at which the sharer could decline — cancelling left a live
// link behind. Here, Cancel leaves nothing.
//
// ⚠ BOTH TOGGLES ARE OFF BY DEFAULT. Notes and screenshots are the two things a
// sharer is most likely to regret: the notes are the candid writing, and a chart
// capture routinely shows the order ladder, working orders and position size
// alongside the chart it was taken for.
// =============================================================================

import { useState } from 'react';
import {
  SHARE_EXPIRY_CHOICES, SHARE_EXPIRY_DEFAULT_INDEX,
} from '../../selectors/share';

export interface ShareChoice {
  notes: boolean;
  shots: boolean;
  /** 0 means never. */
  hours: number;
  /** The wording used in the confirmation, e.g. "4 hours". */
  label: string;
}

export interface ShareDialogProps {
  /** What the link will point at, for the heading — "Day Overview · Thursday…". */
  what: string;
  /** How many trades the report lists. Shown, because it is a disclosure count. */
  tradeCount: number;
  busy: boolean;
  /** Progress while the link is being built ("Copying screenshots…"). */
  status: string | null;
  error: string | null;
  onCreate: (choice: ShareChoice) => void;
  onCancel: () => void;
}

export function ShareDialog(
  { what, tradeCount, busy, status, error, onCreate, onCancel }: ShareDialogProps,
) {
  const [notes, setNotes] = useState(false);
  const [shots, setShots] = useState(false);
  const [index, setIndex] = useState(SHARE_EXPIRY_DEFAULT_INDEX);

  const submit = () => {
    const c = SHARE_EXPIRY_CHOICES[index] ?? SHARE_EXPIRY_CHOICES[SHARE_EXPIRY_DEFAULT_INDEX];
    if (!c) return;
    onCreate({ notes, shots, hours: c.hours, label: c.label });
  };

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Create share link">
      <div className="modal">
        <header className="modal-head">
          <b>Create share link</b>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>×</button>
        </header>
        <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <div className="modal-body">
            <p className="share-lede">
              Anyone with the link can view a <b>read-only</b> snapshot of{' '}
              <b>{what}</b> — no sign-in needed. Treat the link like a password.
            </p>
            {/* ⚠ THE COUNT IS SAID OUT LOUD. The payload describes every trade
                the report lists, whether or not the reader scrolls to it, so
                "this publishes 14 trades" is the honest framing of the click. */}
            <p className="muted share-count">
              {tradeCount === 0
                ? 'No individual trades are included — the report only.'
                : `Includes ${tradeCount} trade${tradeCount === 1 ? '' : 's'}, each openable in a read-only review.`}
            </p>

            <label className="share-opt">
              <input
                type="checkbox" checked={notes} disabled={busy}
                onChange={(e) => setNotes(e.target.checked)}
              />
              <span>
                <b>Include my written notes on each trade</b>
                <span className="muted share-opt-hint">
                  Off by default. Covers per-trade notes, news and mood. Your plan
                  and recap are never shared.
                </span>
              </span>
            </label>

            <label className="share-opt">
              <input
                type="checkbox" checked={shots} disabled={busy}
                onChange={(e) => setShots(e.target.checked)}
              />
              <span>
                <b>Include chart screenshots</b>
                <span className="muted share-opt-hint">
                  ⚠ Off by default. Captures often show more than the chart — order
                  ladder, working orders, position size. Check what is in them
                  before turning this on.
                </span>
              </span>
            </label>

            <div className="field full share-expiry">
              <label className="field-label" htmlFor="share-expiry">Stop working after</label>
              {/* ⚠ The reasoning is on screen, not only in the code. Four hours
                  looks stingy until you have read why. */}
              <span className="muted field-hint">
                Short on purpose — a link that expires costs one click to remake,
                and one that outlives the conversation is a public record of your
                trading. Choose a longer window deliberately.
              </span>
              <select
                id="share-expiry" value={index} disabled={busy}
                onChange={(e) => setIndex(Number(e.target.value))}
              >
                {SHARE_EXPIRY_CHOICES.map((c, i) => (
                  <option key={c.label} value={i}>{c.label}</option>
                ))}
              </select>
            </div>

            <p className="muted share-foot-note">
              You can revoke any link at any time from <b>My Account → Shared links</b>.
            </p>
            {status ? <p className="muted share-status" role="status">{status}</p> : null}
            {error ? <p className="red share-status" role="alert">{error}</p> : null}
          </div>
          <footer className="modal-foot">
            <span className="grow" />
            <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
            <button type="submit" className="btn primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create link'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

export interface ShareCreatedProps {
  link: string;
  /** The expiry wording, or null when the link never expires. */
  expiryLabel: string | null;
  /** False when the clipboard was unavailable — the field is then the only copy. */
  copied: boolean;
  onClose: () => void;
}

export function ShareCreated({ link, expiryLabel, copied, onClose }: ShareCreatedProps) {
  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Share link created">
      <div className="modal">
        <header className="modal-head">
          <b>Share link created</b>
          <button type="button" className="btn" onClick={onClose}>×</button>
        </header>
        <div className="modal-body">
          <p className="share-lede">
            Anyone with this link can view a <b>read-only</b> snapshot — no
            sign-in needed. Treat the link like a password.
          </p>
          {/* ⚠ Selected on mount, so the keyboard path is Ctrl-C. The clipboard
              write can be refused — see `copied` — and then this field is the
              only way the link leaves the screen. */}
          <input
            className="share-link-field mono"
            readOnly
            value={link}
            ref={(el) => { el?.focus(); el?.select(); }}
          />
          <p className="muted share-foot-note">
            {copied ? 'Copied to your clipboard. ' : 'Copy it from the box above. '}
            {expiryLabel
              ? <>Stops working <b>{expiryLabel.toLowerCase()}</b> from now.</>
              : <><b>Never expires.</b></>}
            {' '}Revoke it any time from <b>My Account → Shared links</b>.
          </p>
        </div>
        <footer className="modal-foot">
          <span className="grow" />
          <button type="button" className="btn primary" onClick={onClose}>Done</button>
        </footer>
      </div>
    </div>
  );
}
